/**
 * Single source of the Ops Inbox risk-score bases and due-date bonuses.
 * opsInboxService.riskScore() computes with these; the Legend help text in
 * pageHelpContent.ts prints from the same constants so the two cannot drift
 * (PTUX-065). Higher means more urgent; the score is capped at 100.
 */
export type OpsRiskPriority = 'CRITICAL' | 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';

export const OPS_RISK_PRIORITY_BASES: Record<OpsRiskPriority, number> = {
  CRITICAL: 92,
  URGENT: 82,
  HIGH: 68,
  MEDIUM: 44,
  LOW: 24,
};

/** Bonus added by due-date proximity, checked in order. */
export const OPS_RISK_DUE_BONUSES: ReadonlyArray<{ withinHours: number; bonus: number; label: string }> = [
  { withinHours: 0, bonus: 12, label: 'overdue' },
  { withinHours: 6, bonus: 10, label: 'due within 6 hours' },
  { withinHours: 24, bonus: 7, label: 'due within 24 hours' },
  { withinHours: 72, bonus: 3, label: 'due within 72 hours' },
];

export const OPS_RISK_SCORE_CAP = 100;

export function opsRiskScore(priority: OpsRiskPriority, dueAt?: string | null, now = Date.now()): number {
  const base = OPS_RISK_PRIORITY_BASES[priority];
  if (!dueAt) return base;
  const due = Date.parse(dueAt);
  if (Number.isNaN(due)) return base;
  const hours = (due - now) / 36e5;
  for (const step of OPS_RISK_DUE_BONUSES) {
    if (hours <= step.withinHours) return Math.min(OPS_RISK_SCORE_CAP, base + step.bonus);
  }
  return base;
}
